// Verifiable-citation extraction for the chat UI.
//
// Every bio MCP tool result can carry a verifiable `_meta.citation` (see
// packages/mcp-shared/src/provenance/provenance.ts): a content-addressed,
// re-checkable attribution — upstream source (+ license), the tool, an ISO
// timestamp, and a sha256 `result_hash` of the returned bytes. The chat
// transport surfaces these inside a tool part's `output`, sometimes nested:
// direct-tool calls expose `structuredContent._meta.citation`; the Code Mode
// sandbox aggregates per-call citations under a top-level `citations` array.
//
// `collectCitations` deep-walks an arbitrary tool output and returns every
// embedded citation, deduped by source+server+tool+`result_hash`, so the UI can
// render a Sources strip regardless of how the result was produced.

export interface Citation {
  source: {
    id: string;
    name: string;
    url?: string;
    license?: string;
    version?: string;
  };
  server: string;
  tool: string;
  retrieved_at: string;
  query_hash: string;
  result_hash: string;
  record_count?: number;
  data_access_id?: string;
  text: string;
}

/** Structural check — matches the provenance.ts Citation shape. */
function isCitation(value: unknown): value is Citation {
  if (value === null || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (typeof v.result_hash !== "string" || typeof v.text !== "string") return false;
  const src = v.source;
  if (src === null || typeof src !== "object") return false;
  return typeof (src as Record<string, unknown>).name === "string";
}

/**
 * Iterative, cycle-safe deep-walk that invokes `onCitation` for every embedded
 * {@link Citation}. Cycle-safe via the WeakSet (each object visited once ⇒
 * terminates), iterative (explicit stack, so deep nesting can't overflow), and —
 * deliberately — with NO depth/node cap: a 5000-node bound silently dropped
 * citations on big multi-tool turns (the server mirror logged 7 where the UI
 * showed 10). Inputs are size-capped upstream, so a complete walk stays cheap.
 * Children are pushed in reverse so the LIFO stack yields first-seen (in-order);
 * each property read is guarded so one throwing getter skips itself, not the
 * siblings around it (render-path safety).
 */
function walkCitations(value: unknown, onCitation: (c: Citation) => void): void {
  const visited = new WeakSet<Record<string, unknown>>();
  const stack: unknown[] = [value];
  while (stack.length > 0) {
    const node = stack.pop();
    if (node === null || typeof node !== "object") continue;
    if (visited.has(node as Record<string, unknown>)) continue;
    visited.add(node as Record<string, unknown>);
    if (isCitation(node)) {
      onCitation(node);
      continue; // a citation has no nested citations worth scanning
    }
    if (Array.isArray(node)) {
      for (let i = node.length - 1; i >= 0; i--) stack.push(node[i]);
      continue;
    }
    const obj = node as Record<string, unknown>;
    const keys = Object.keys(obj);
    for (let i = keys.length - 1; i >= 0; i--) {
      try {
        stack.push(obj[keys[i]]);
      } catch {
        continue; // a throwing property getter — skip it, keep the siblings
      }
    }
  }
}

/**
 * Return every embedded {@link Citation} from an arbitrary tool output, deduped
 * by source+server+tool+`result_hash` in first-seen order. Complete (no
 * truncation) and render-path safe via {@link walkCitations}.
 */
export function collectCitations(value: unknown): Citation[] {
  const out: Citation[] = [];
  const seen = new Set<string>();
  walkCitations(value, (c) => {
    // Dedupe on source+server+tool+hash, not result_hash alone: two distinct
    // sources can return byte-identical payloads (same hash) and must both be
    // shown. Genuine re-fetches of the same data still collapse.
    const key = `${c.source.id || c.source.name}|${c.server}|${c.tool}|${c.result_hash}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(c);
  });
  return out;
}
